import { useState, useCallback } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useAdminOverride } from "./useAdminOverride";

// Use our Netlify function proxy to bypass CORS 🎉
const GRAPHQL_ENDPOINT = "/.netlify/functions/graphql-proxy";

/**
 * Extract the operation name from a GraphQL query string
 * e.g., "query GetUsers { ... }" -> "GetUsers"
 */
function extractOperationName(query: string): string | undefined {
  const match = query.match(/^\s*(query|mutation|subscription)\s+(\w+)/i);
  return match?.[2];
}

interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: Array<{ message: string; locations?: unknown; path?: unknown }>;
}

interface UseGraphQLReturn<T = unknown> {
  data: T | null;
  errors: string[] | null;
  isLoading: boolean;
  execute: (query: string, variables?: Record<string, unknown>) => Promise<void>;
  rawResponse: GraphQLResponse<T> | null;
}

/**
 * Hook for making GraphQL queries to dashboard-api.mavenmm.com
 * Uses the Teamwork auth token from useAuthContext
 *
 * No mutations allowed - we're just tourists here 🏖️
 */
export function useGraphQL<T = unknown>(): UseGraphQLReturn<T> {
  const { getAccessToken } = useAuthContext();
  const { overrideEmail } = useAdminOverride();
  const [data, setData] = useState<T | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rawResponse, setRawResponse] = useState<GraphQLResponse<T> | null>(null);

  const execute = useCallback(
    async (query: string, variables?: Record<string, unknown>) => {
      const token = getAccessToken();
      if (!token) {
        setErrors(["No access token available. Are you logged in? 🤔"]);
        return;
      }

      // Safety check - no mutations allowed!
      if (query.trim().toLowerCase().startsWith("mutation")) {
        setErrors(["Nice try! Mutations are disabled. Read-only vibes only 🚫"]);
        return;
      }

      setIsLoading(true);
      setErrors(null);
      setData(null);
      setRawResponse(null);

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        };

        // Add admin override header if set (for impersonation)
        if (overrideEmail) {
          headers["x-admin-user-override"] = overrideEmail;
        }

        const operationName = extractOperationName(query);
        const response = await fetch(GRAPHQL_ENDPOINT, {
          method: "POST",
          credentials: "include", // Send cookies for token exchange
          headers,
          body: JSON.stringify({ query, variables, operationName }),
        });

        // Try to get response body even on error (proxy sends helpful details)
        const responseText = await response.text();

        let json: GraphQLResponse<T> & { error?: string; details?: string; hint?: string };

        try {
          json = JSON.parse(responseText);
        } catch {
          throw new Error(`Non-JSON response (${response.status}): ${responseText.slice(0, 200)}`);
        }

        if (!response.ok) {
          // Extract error details from proxy response
          const errorMsg = json.error || `HTTP ${response.status}`;
          const details = json.details ? ` - ${json.details}` : "";
          const hint = json.hint ? ` (${json.hint})` : "";
          throw new Error(`${errorMsg}${details}${hint}`);
        }
        setRawResponse(json);

        if (json.errors && json.errors.length > 0) {
          setErrors(json.errors.map((e) => e.message));
        }

        if (json.data) {
          setData(json.data);
        }
      } catch (error) {
        setErrors([
          error instanceof Error
            ? error.message
            : "Something went wrong. Probably not your fault (probably) 🤷",
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken, overrideEmail]
  );

  return { data, errors, isLoading, execute, rawResponse };
}
